import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function PushNotificationToggle() {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const checkSubscription = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    setSupported(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    } catch {}
  }, []);

  useEffect(() => { checkSubscription(); }, [checkSubscription]);

  const subscribe = async () => {
    if (!VAPID_PUBLIC_KEY) { toast.error("Configuration push manquante"); return; }
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      await axios.post(`${API}/api/push/subscribe`, { subscription: sub.toJSON() }, { headers });
      setIsSubscribed(true);
      toast.success("Notifications push activees !");
    } catch (err) {
      if (Notification.permission === "denied") {
        toast.error("Notifications bloquees dans votre navigateur. Modifiez les parametres du site.");
      } else {
        toast.error("Erreur d'activation des notifications");
      }
    } finally { setLoading(false); }
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
      await axios.post(`${API}/api/push/unsubscribe`, {}, { headers });
      setIsSubscribed(false);
      toast.success("Notifications push desactivees");
    } catch { toast.error("Erreur de desactivation"); }
    finally { setLoading(false); }
  };

  if (!supported) return null;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={isSubscribed ? unsubscribe : subscribe}
      disabled={loading}
      className={`text-xs gap-1.5 ${isSubscribed ? "text-[#0099CC]" : "text-gray-500 hover:text-white"}`}
      data-testid="push-toggle"
      title={isSubscribed ? "Desactiver les notifications push" : "Activer les notifications push"}
    >
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : isSubscribed ? (
        <Bell className="h-3.5 w-3.5 fill-[#0099CC]" />
      ) : (
        <BellOff className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}
